﻿// RemotePage — loads the Angular ClaraAI app as a Module Federation remote
// using the @angular-architects/native-federation protocol.
//
// Integration flow:
//   1. fetch(remoteEntryUrl)  →  native-federation manifest JSON
//      { name, exposes: { './mount': '<absolute-url>' } }
//   2. import(/* @vite-ignore */ exposes['./mount'])
//      →  { mount(container, opts): Promise<HTMLElement>, unmount(container, el) }
//   3. mount(containerDiv)  →  registers <clara-ai-chat-element> + appends it.
//   4. On every React location change: attempt to sync the Angular router via
//      the custom element injector; fall back to a popstate event.
//
// The component stays self-contained — no build plugin needed on the host.

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { kciEventBus } from '../../eventBus/KCiEventBus';
import { environment } from '../../environments/environment';

type ClaraContext = Array<{ key: string; value: unknown }>;

function postClaraContext(context: ClaraContext): void {
  const contextIntegrationId = context.find(({ key }) => key === 'aiIntegrationId')?.value;
  const aiIntegrationId = typeof contextIntegrationId === 'string' && contextIntegrationId.trim()
    ? contextIntegrationId
    : environment.aiIntegrationId;

  window.postMessage({
    type: 'CONTEXT_UPDATE',
    message: {
      context,
      aiintegrationid: aiIntegrationId,
    },
  }, window.location.origin);
} 
 
type RemoteStatus = 'loading' | 'ready' | 'error';
type RemoteHashRoutingMode = 'always' | 'chat-only' | 'never';

interface RemotePageProps {
  /** Display label used in loading / error messages. */
  remoteName: string;
  /**
   * Full URL to the Angular remote's remoteEntry.json, e.g.
   * "http://localhost:4100/remoteEntry.json".
   * Overrides remotePort when supplied.
   */
  remoteEntryUrl?: string;
  /**
   * Federation remote name declared in federation.config.js (default: "ai-chat").
   * Used only in error messages.
   */
  remoteRuntimeName?: string;
  /** Custom-element tag registered by the Angular remote (default: "clara-ai-chat-element"). */
  remoteElementTag?: string;
  /** CSS class(es) forwarded to the Angular custom element root. */
  className?: string;
  /** Chat session to load after the remote has mounted. */
  sessionIdToLoad?: string | null;
  /** Optional mapper: host URL path -> remote URL path. */
  mapHostToRemotePath?: (hostPath: string) => string;
  /** Optional mapper: remote URL path -> host URL path. */
  mapRemoteToHostPath?: (remotePath: string) => string;
  /** Controls whether the embedded Angular remote should use hash routing. */
  remoteHashRouting?: RemoteHashRoutingMode;
  /**
   * Keep the host URL fixed while the remote handles its internal routing.
   * When true, host is locked to the mount-time URL; when a string, host is
   * locked to that exact path (e.g. "/").
   */
  preserveHostPath?: boolean | string;
  /**
   * Fully decouples the embedded remote from the host's React Router in
   * BOTH directions — no host-location → remote-router push, no
   * remote-navigation → host-router push, no "lock host path" redirect.
   * Use this for a side-panel-style embed (e.g. AIPanel) that must never
   * touch whatever tab/route the host happens to be on. `preserveHostPath`
   * still actively navigates the host to re-lock it on drift, which is the
   * wrong behavior for a panel — this flag skips all of that instead.
   */
  standalone?: boolean;
  /** Called after the remote custom element has mounted and can receive events. */
  onReady?: () => void;
  /**
   * Convenience shorthand — constructs remoteEntryUrl as
   * "http://localhost:<remotePort>/remoteEntry.json".
   * Ignored when remoteEntryUrl is provided. Default: 4100.
   */
  remotePort?: number;
}

type MountFn = (
  container: HTMLElement,
  options?: { className?: string; onNavigate?: (path: string) => void },
) => Promise<HTMLElement>;
type UnmountFn = (container: HTMLElement, element?: HTMLElement) => void;
type NavigateFn = (path: string) => Promise<void>;

export const RemotePage = React.memo(function RemotePage({
  remoteName,
  remoteEntryUrl,
  remoteRuntimeName = 'ai-chat',
  remoteElementTag: _remoteElementTag = 'clara-ai-chat-element',
  className,
  sessionIdToLoad,
  mapHostToRemotePath,
  mapRemoteToHostPath,
  remoteHashRouting = 'chat-only',
  preserveHostPath = false,
  standalone = false,
  onReady,
  remotePort = 4100,
}: RemotePageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedElRef  = useRef<HTMLElement | null>(null);
  const unmountFnRef  = useRef<UnmountFn | null>(null);
  const navigateFnRef = useRef<NavigateFn | null>(null);
  const loadedRef     = useRef(false);
  const lastPathFromRemoteRef = useRef<string>('');
  const [status, setStatus] = useState<RemoteStatus>('loading');
  const [errorDetail, setErrorDetail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Keep navigate/location in refs so the load effect never depends on them.
  // This prevents Angular from being unmounted/remounted on every URL change.
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const onReadyRef = useRef(onReady);
  const initialHostPathRef = useRef(location.pathname + location.search + location.hash);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { locationRef.current = location; }, [location]);

  useEffect(() => {
    if (!standalone) return;

    const publishContext = (context?: ClaraContext) => {
      if (context?.length) postClaraContext(context);
    };
    const unsubscribe = kciEventBus.subscribe<{ context?: ClaraContext }>(
      'ClaraContextUpdated',
      (event) => publishContext(event.payload.context),
    );

    publishContext(kciEventBus.getLatest<{ context?: ClaraContext }>('ClaraContextUpdated')?.payload.context);
    return unsubscribe;
  }, [standalone]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  const entryUrl = remoteEntryUrl ?? `http://localhost:${remotePort}/remoteEntry.json`;

  // Default host path -> remote path mapping (chat container behavior).
  const defaultToRemotePath = (hostPath: string) => {
    if (hostPath === '/chat' || hostPath.startsWith('/chat?')) return '/chat';
    return hostPath;
  };

  // Default remote path -> host path mapping (chat container behavior).
  // Always keep the parent URL at /chat — child-internal routes must not
  // propagate to the parent and cause a re-mount.
  const defaultToHostPath = (_remotePath: string) => '/chat';

  const toRemotePath = mapHostToRemotePath ?? defaultToRemotePath;
  const toHostPath = mapRemoteToHostPath ?? defaultToHostPath;

  // ── Load + mount the Angular remote ──────────────────────────────────────
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const hostWindow = window as Window & {
      __CLARA_REMOTE__?: boolean;
      __CLARA_REMOTE_HASH_MODE__?: RemoteHashRoutingMode;
    };
    const prevRemoteFlag = hostWindow.__CLARA_REMOTE__;
    const prevHashMode = hostWindow.__CLARA_REMOTE_HASH_MODE__;

    hostWindow.__CLARA_REMOTE__ = true;
    hostWindow.__CLARA_REMOTE_HASH_MODE__ = remoteHashRouting;

    // cancelled + abortController guard against React StrictMode's
    // intentional double-invocation: if cleanup fires while the async load
    // is still in-flight, we cancel the fetch and skip the mount so only
    // one clara-ai-chat-element ever gets appended to the DOM.
    let cancelled = false;
    const abortController = new AbortController();

    const load = async () => {
      try {
        // 1. Fetch the native-federation manifest from the Angular remote.
        const resp = await fetch(entryUrl, { signal: abortController.signal });
        if (!resp.ok) {
          throw new Error(
            `Cannot reach Angular remote at ${entryUrl} (HTTP ${resp.status}). ` +
            `Make sure "npm run serve-mf" is running in the Angular project.`
          );
        }
        const manifest = await resp.json() as {
          exposes?: Array<{ key: string; outFileName: string }>;
        };

        if (cancelled) return;

        // 2. Resolve the absolute URL for the ./mount module.
        // Native Federation serves exposes as an array: [{ key: './mount', outFileName: 'mount-ABC.js' }]
        const mountEntry = Array.isArray(manifest?.exposes)
          ? manifest.exposes.find(e => e.key === './mount')
          : undefined;

        if (!mountEntry?.outFileName) {
          throw new Error(
            `Remote "${remoteRuntimeName}" remoteEntry.json does not contain an exposes["./mount"] entry. ` +
            `Check federation.config.js in the Angular project.`
          );
        }

        // Construct the absolute URL for the mount module
        const baseUrl = entryUrl.replace('/remoteEntry.json', '');
        const mountModuleUrl = `${baseUrl}/${mountEntry.outFileName}`;

        // 3. Dynamically import the compiled mount module.
        //    @vite-ignore tells Vite to skip static analysis of this URL.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore – runtime URL, cannot be statically resolved
        const mountModule = await import(/* @vite-ignore */ mountModuleUrl) as {
          mount?: MountFn;
          unmount?: UnmountFn;
          navigate?: NavigateFn;
          default?: { mount?: MountFn; unmount?: UnmountFn; navigate?: NavigateFn };
        };

        if (cancelled) return;

        const mountFn: MountFn | undefined =
          mountModule.mount ?? mountModule.default?.mount;
        const unmountFn: UnmountFn | undefined =
          mountModule.unmount ?? mountModule.default?.unmount;
        const navigateFn: NavigateFn | undefined =
          mountModule.navigate ?? mountModule.default?.navigate;

        if (typeof mountFn !== 'function') {
          throw new Error(
            `"./mount" module from "${remoteRuntimeName}" does not export a mount() function.`
          );
        }

        // 4. Mount the custom element into the container.
        if (!cancelled && containerRef.current) {
          const el = await mountFn(containerRef.current, {
            className,
            onNavigate: (remotePath) => {
              if (standalone || preserveHostPath) return;
              const hostPath = toHostPath(remotePath);
              if (!hostPath || hostPath === locationRef.current.pathname) return;
              lastPathFromRemoteRef.current = hostPath;
              navigateRef.current(hostPath, { replace: false });
            },
          });

          // Guard: cleanup may have fired during the await above.
          if (cancelled) {
            if (typeof unmountFn === 'function') unmountFn(containerRef.current, el);
            else containerRef.current.innerHTML = '';
            return;
          }

          mountedElRef.current = el;
          unmountFnRef.current = unmountFn ?? null;
          navigateFnRef.current = navigateFn ?? null;
          setStatus('ready');
          onReadyRef.current?.();
        }
      } catch (err) {
        // Suppress abort errors from cleanup cancellation.
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[RemotePage] Failed to load "${remoteName}":`, err);
        setErrorDetail(msg);
        setStatus('error');
      }
    };

    load();

    return () => {
      cancelled = true;
      abortController.abort();
      // Reset so a genuine remount (e.g. tab switch) re-loads properly.
      loadedRef.current = false;
      setStatus('loading');
      // Cleanup on unmount
      const el  = mountedElRef.current;
      const ufn = unmountFnRef.current;
      if (containerRef.current && el) {
        if (typeof ufn === 'function') {
          ufn(containerRef.current, el);
        } else {
          containerRef.current.innerHTML = '';
        }
      }
      mountedElRef.current  = null;
      unmountFnRef.current  = null;
      navigateFnRef.current = null;

      hostWindow.__CLARA_REMOTE__ = prevRemoteFlag;
      hostWindow.__CLARA_REMOTE_HASH_MODE__ = prevHashMode;
    };
  // Only re-mount when the remote entry URL itself changes.
  // navigate and location are accessed via refs to avoid spurious unmounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryUrl, remoteHashRouting]);

  // ── Sync React location → Angular router ─────────────────────────────────
  useEffect(() => {
    if (standalone || status !== 'ready' || !preserveHostPath) return;

    const lockedHostPath =
      typeof preserveHostPath === 'string'
        ? preserveHostPath
        : initialHostPathRef.current;
    const currentHostPath = location.pathname + location.search + location.hash;

    if (currentHostPath !== lockedHostPath) {
      navigateRef.current(lockedHostPath, { replace: true });
    }
  }, [location, preserveHostPath, standalone, status]);

  // ── Sync React location → Angular router ─────────────────────────────────
  useEffect(() => {
    if (standalone || status !== 'ready') return;

    const sourcePath = preserveHostPath
      ? (typeof preserveHostPath === 'string'
          ? preserveHostPath
          : initialHostPathRef.current)
      : (location.pathname + location.search + location.hash);

    const target = toRemotePath(sourcePath);

    // Skip if this location change was triggered by the remote itself.
    if (lastPathFromRemoteRef.current === target) {
      lastPathFromRemoteRef.current = '';
      return;
    }

    navigateFnRef.current?.(target).catch(() => {
      // Keep host stable even if remote navigation fails.
    });
  }, [location, standalone, status]);

  useEffect(() => {
    if (status !== 'ready' || sessionIdToLoad === undefined) return;

    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('kci-load-chat-session', {
        detail: { sessionId: sessionIdToLoad },
      }));
    }, 0);

    return () => window.clearTimeout(timer);
  }, [sessionIdToLoad, status]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="remote-page" style={{ width: '100%', height: '100%'}}>
      {status === 'loading' && (
        <div
          style={{ padding: '64px 40px', textAlign: 'center', color: '#666' }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 36, display: 'block', marginBottom: 16, color: '#0c233c' }}
          >
            progress_activity
          </span>
          <p style={{ margin: 0, fontWeight: 500 }}>Loading {remoteName}…</p>
          <p style={{ margin: '8px 0 0', fontSize: 13 }}>
            Fetching remote entry from {entryUrl}
          </p>
        </div>
      )}

      {status === 'error' && (
        <div
          style={{ padding: '64px 40px', textAlign: 'center', color: '#c62828' }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 36, display: 'block', marginBottom: 16 }}
          >
            error_outline
          </span>
          <p style={{ margin: 0, fontWeight: 600 }}>Remote module unavailable</p>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#666' }}>
            {errorDetail}
          </p>
          <p
            style={{
              margin: '16px auto 0',
              fontSize: 12,
              color: '#888',
              maxWidth: 400,
              lineHeight: 1.6,
            }}
          >
            Start the Angular remote then refresh:
            <br />
            <code
              style={{
                display: 'inline-block',
                marginTop: 8,
                padding: '4px 10px',
                background: '#f5f7fa',
                borderRadius: 4,
                border: '1px solid #dce1e9',
                fontFamily: 'monospace',
                color: '#0c233c',
              }}
            >
              npm run serve-mf
            </code>
          </p>
        </div>
      )}

      {/* Angular custom element mount point */}
      <div
        ref={containerRef}
        id={`mf-${remoteName.replace(/\s+/g, '-').toLowerCase()}`}
        style={{
          display: status === 'ready' ? 'block' : 'none',
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
});

export default RemotePage;

